import { useEffect, useState } from "react";
import { Star, Loader2, Check, Ban, Eye, Wallet, Settings2, BarChart3, X } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString();
const TABS = [["creators", "Creators"], ["rewards", "Reward Rule"], ["claims", "Payout Requests"]];

function ReportModal({ creatorId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    endpoints.adminCreatorReport(creatorId).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [creatorId]);
  const st = data?.stats;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="admin-report-modal">
      <div className="bg-[#121620] border border-[#273042] rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#121620] border-b border-[#273042] p-4 flex items-center justify-between">
          <p className="text-white font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#E11D2A]" /> {data?.creator?.name || "Report"}</p>
          <button onClick={onClose} data-testid="report-modal-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div> : !data ? <p className="text-slate-400 text-center py-12">Report unavailable.</p> : (
          <div className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {(data.channels || []).map((ch) => (
                <div key={ch.key} className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3" data-testid={`admin-channel-${ch.key}`}>
                  <p className="text-xs text-slate-400">{ch.label}</p>
                  <p className="text-lg font-extrabold text-white">{fmt(ch.views)}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3"><p className="text-xs text-slate-400">Total (all-time)</p><p className="text-lg font-extrabold text-white">{fmt(st?.totalEligibleViews)}</p></div>
              <div className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3"><p className="text-xs text-slate-400">Remaining payable</p><p className="text-lg font-extrabold text-green-400">{fmt(st?.payableViews)}</p></div>
              <div className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3"><p className="text-xs text-slate-400">Cashed-out views</p><p className="text-lg font-extrabold text-slate-300">{fmt(st?.consumedViews)}</p></div>
            </div>
            <p className="text-white font-semibold text-sm mb-2">Cash-out history (permanent audit)</p>
            {(data.claims || []).length === 0 ? <p className="text-slate-500 text-sm">No cash-outs yet.</p> : (
              <div className="overflow-x-auto rounded-xl border border-[#273042]">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0C10] text-slate-400"><tr>
                    <th className="text-left py-2.5 px-3">Requested</th><th className="text-left py-2.5 px-2">Paid at</th>
                    <th className="py-2.5 px-2">Views paid</th><th className="py-2.5 px-2">Amount</th><th className="py-2.5 px-2">Ref</th><th className="py-2.5 px-3">Status</th>
                  </tr></thead>
                  <tbody>
                    {data.claims.map((cl) => (
                      <tr key={cl.id} className="border-t border-[#273042] text-slate-200">
                        <td className="py-2.5 px-3 whitespace-nowrap">{cl.createdAt ? new Date(cl.createdAt).toLocaleString() : "—"}</td>
                        <td className="py-2.5 px-2 whitespace-nowrap">{cl.paidAt ? new Date(cl.paidAt).toLocaleString() : "—"}</td>
                        <td className="text-center">{fmt(cl.viewsIncluded || (cl.rewardUnits || 0) * (cl.viewsPerReward || 0))}</td>
                        <td className="text-center font-semibold text-white">{fmt(cl.amountFrw)} RWF</td>
                        <td className="text-center text-xs text-slate-500">{cl.mtnRef || cl.payoutRef || "—"}</td>
                        <td className="text-center capitalize"><span className={cl.status === "paid" ? "text-green-400" : cl.status === "rejected" || cl.status === "failed" ? "text-red-400" : "text-amber-400"}>{cl.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCreators() {
  const [tab, setTab] = useState("creators");
  const [creators, setCreators] = useState([]);
  const [claims, setClaims] = useState([]);
  const [cfg, setCfg] = useState({ viewsPerReward: 50000, rewardFrw: 1000000 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reportFor, setReportFor] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      endpoints.adminCreators().then((r) => setCreators(r.data?.creators || [])),
      endpoints.adminCreatorClaims().then((r) => setClaims(r.data?.claims || [])),
      endpoints.adminCreatorRewardConfig().then((r) => setCfg(r.data || cfg)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await endpoints.adminSetCreatorStatus(id, status); toast.success(`Creator ${status}.`); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  const saveCfg = async () => {
    setBusy(true);
    try { await endpoints.adminSetCreatorRewardConfig({ viewsPerReward: Number(cfg.viewsPerReward), rewardFrw: Number(cfg.rewardFrw) }); toast.success("Reward rule saved."); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const act = async (fn, id, body) => {
    try { const r = await fn(id, body); toast.success(r.data?.status ? `Payout ${r.data.status}.` : "Done."); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  const badge = (s) => ({ approved: "bg-green-500/15 text-green-400", pending: "bg-amber-500/15 text-amber-400", suspended: "bg-red-500/15 text-red-400", rejected: "bg-red-500/15 text-red-400", paid: "bg-green-500/15 text-green-400", processing: "bg-blue-500/15 text-blue-400", failed: "bg-red-500/15 text-red-400" }[s] || "bg-slate-500/15 text-slate-400");

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>;

  return (
    <div data-testid="admin-creators-page">
      <h1 className="text-2xl font-extrabold text-white flex items-center gap-2 mb-1"><Star className="w-6 h-6 text-[#E11D2A]" /> Creators &amp; Celebrities</h1>
      <p className="text-slate-400 text-sm mb-6">Approve creators, configure rewards and process payouts.</p>

      <div className="flex gap-2 mb-6 border-b border-[#273042]">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`creators-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "text-white border-[#E11D2A]" : "text-slate-400 border-transparent hover:text-white"}`}>
            {l}{k === "claims" && claims.filter((c) => c.status === "pending").length > 0 ? ` (${claims.filter((c) => c.status === "pending").length})` : ""}
          </button>
        ))}
      </div>

      {tab === "creators" && (
        <div className="space-y-3" data-testid="creators-list">
          {creators.length === 0 && <p className="text-slate-500">No creators yet.</p>}
          {creators.map((c) => (
            <div key={c.id} className="rounded-xl bg-[#121620] border border-[#273042] p-4 flex flex-wrap items-center gap-4" data-testid={`admin-creator-${c.id}`}>
              <div className="w-11 h-11 rounded-full bg-[#0A0C10] border border-[#273042] flex items-center justify-center shrink-0 overflow-hidden">
                {c.photoUrl ? <img src={c.photoUrl} alt="" className="w-full h-full object-cover" /> : <Star className="w-5 h-5 text-[#E11D2A]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold truncate">{c.name} <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${badge(c.status)}`}>{c.status}</span></p>
                <p className="text-xs text-slate-500">{c.category} · <Eye className="w-3 h-3 inline" /> {fmt(c.stats?.totalEligibleViews)} views · {fmt(c.stats?.videoCount)} videos · {fmt(c.stats?.availableAmountFrw)} RWF available</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setReportFor(c.id)} data-testid={`report-creator-${c.id}`} className="h-9 px-3 rounded-lg bg-[#E11D2A]/15 text-[#E11D2A] text-sm font-semibold flex items-center gap-1"><BarChart3 className="w-4 h-4" /> Report</button>
                {c.status !== "approved" && <button onClick={() => setStatus(c.id, "approved")} data-testid={`approve-creator-${c.id}`} className="h-9 px-3 rounded-lg bg-green-600/20 text-green-400 text-sm font-semibold flex items-center gap-1"><Check className="w-4 h-4" /> Approve</button>}
                {c.status !== "suspended" && <button onClick={() => setStatus(c.id, "suspended")} data-testid={`suspend-creator-${c.id}`} className="h-9 px-3 rounded-lg bg-red-600/20 text-red-400 text-sm font-semibold flex items-center gap-1"><Ban className="w-4 h-4" /> Suspend</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "rewards" && (
        <div className="max-w-md rounded-xl bg-[#121620] border border-[#273042] p-6" data-testid="reward-config">
          <p className="text-white font-semibold flex items-center gap-2 mb-4"><Settings2 className="w-5 h-5 text-[#E11D2A]" /> Reward rule</p>
          <label className="text-sm text-slate-300">Verified views required</label>
          <input type="number" value={cfg.viewsPerReward} onChange={(e) => setCfg({ ...cfg, viewsPerReward: e.target.value })} data-testid="cfg-views"
            className="w-full h-11 px-4 mt-1 mb-4 rounded-lg bg-[#0A0C10] border border-[#273042] text-white outline-none focus:border-[#E11D2A]" />
          <label className="text-sm text-slate-300">Reward amount (RWF)</label>
          <input type="number" value={cfg.rewardFrw} onChange={(e) => setCfg({ ...cfg, rewardFrw: e.target.value })} data-testid="cfg-amount"
            className="w-full h-11 px-4 mt-1 mb-4 rounded-lg bg-[#0A0C10] border border-[#273042] text-white outline-none focus:border-[#E11D2A]" />
          <p className="text-xs text-slate-500 mb-4">Current rule: <b className="text-white">{fmt(cfg.viewsPerReward)} views = {fmt(cfg.rewardFrw)} RWF</b></p>
          <button onClick={saveCfg} disabled={busy} data-testid="save-reward-cfg" className="h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold disabled:opacity-60">Save rule</button>
        </div>
      )}

      {tab === "claims" && (
        <div className="space-y-3" data-testid="claims-list">
          {claims.length === 0 && <p className="text-slate-500">No payout requests.</p>}
          {claims.map((c) => (
            <div key={c.id} className="rounded-xl bg-[#121620] border border-[#273042] p-4 flex flex-wrap items-center gap-4" data-testid={`claim-${c.id}`}>
              <Wallet className="w-5 h-5 text-[#E11D2A] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold">{c.creatorName} · {fmt(c.amountFrw)} RWF <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${badge(c.status)}`}>{c.status}</span></p>
                <p className="text-xs text-slate-500">{fmt(c.viewsClaimed)} views · {c.rewardUnits} unit(s) · {c.payoutPhone}</p>
              </div>
              <div className="flex gap-2">
                {c.status === "pending" && <button onClick={() => act(endpoints.adminApproveCreatorClaim, c.id, {})} data-testid={`approve-claim-${c.id}`} className="h-9 px-3 rounded-lg bg-green-600/20 text-green-400 text-sm font-semibold">Approve &amp; Pay</button>}
                {c.status === "failed" && <button onClick={() => act(endpoints.adminRetryCreatorClaim, c.id)} className="h-9 px-3 rounded-lg bg-blue-600/20 text-blue-400 text-sm font-semibold">Retry</button>}
                {["pending", "approved", "processing", "failed"].includes(c.status) && <button onClick={() => act(endpoints.adminMarkCreatorClaimPaid, c.id, {})} data-testid={`markpaid-claim-${c.id}`} className="h-9 px-3 rounded-lg bg-white/5 border border-[#273042] text-slate-200 text-sm font-semibold">Mark paid</button>}
                {c.status === "pending" && <button onClick={() => act(endpoints.adminRejectCreatorClaim, c.id, { reason: "Rejected" })} className="h-9 px-3 rounded-lg bg-red-600/20 text-red-400 text-sm font-semibold">Reject</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {reportFor && <ReportModal creatorId={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}
